import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/checkbox?tab=designer).
 *
 * @slot default - Slot para o rótulo do checkbox, como alternativa à propriedade label.
 */
@Component({
  tag: 'br-checkbox',
  styleUrl: 'checkbox.scss',
  shadow: true,
  formAssociated: true,
})
export class Checkbox {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrCheckboxElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Define o estado intermediário do checkbox.
   * Quando verdadeiro, exibe uma marcação parcial visual que indica seleção parcial.
   * Útil para representar grupos onde alguns itens estão selecionados, mas não todos.
   * Ao clicar no checkbox neste estado, ele será automaticamente alterado para marcado.
   */
  @Prop({ reflect: true, mutable: true }) indeterminate: boolean = false

  /**
   * Define o estado de seleção do checkbox.
   * Se definido como verdadeiro, o checkbox estará marcado. Caso contrário, estará desmarcado.
   */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-checkbox-${checkboxId++}`

  /**
   * Desativa o checkbox, tornando-o não interativo.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false

  /**
   * Define se o label associado ao checkbox deve ser oculto.
   * Se definido como verdadeiro, o texto do label será oculto, mas o checkbox ainda estará visível e funcional.
   */
  @Prop({ reflect: true }) readonly hasHiddenLabel: boolean = false

  /**
   * Texto descritivo exibido à direita do checkbox.
   * Caso um slot seja utilizado para fornecer um texto alternativo, o valor desta propriedade será ignorado.
   */
  @Prop({ reflect: true }) readonly label: string

  /**
   * Define o nome do checkbox, que é utilizado para agrupar checkboxes em formulários e identificar o campo.
   * O valor é obrigatório e deve ser fornecido para garantir o correto funcionamento em formulários.
   */
  @Prop({ reflect: true }) readonly name!: string

  /**
   * Indica a validade do checkbox.
   * Se não for especificado, o valor padrão é `null`, indicando que a validade não foi definida.
   */
  @Prop({ reflect: true }) readonly state: 'valid' | 'invalid'

  /**
   * Define o valor associado ao checkbox quando ele faz parte de um formulário nativo (`<form>`).
   * Esse valor é enviado com o formulário quando o checkbox está selecionado.
   * **Nota:** Esta propriedade não deve ser utilizada para determinar se o checkbox está selecionado; para verificar o estado de seleção, use a propriedade `checked`.
   */
  @Prop({ reflect: true }) readonly value: string

  /**
   * Indica se o checkbox é pai de um grupo de checkboxes.
   */
  @Prop({ reflect: false }) readonly isFather: boolean = false

  /**
   * Indica se o slot default possui conteúdo.
   */
  @State() slotDefault: boolean = false

  /**
   * Define o número de checkboxes filhos em um grupo de checkboxes.
   */
  @State() numberOfChildren: number = 0

  /**
   * Disparado depois que o valor do `checked` foi alterado
   */
  @Event() checkedChange: EventEmitter<boolean>

  /**
   * Disparado depois que o valor do `indeterminate` foi alterado.
   */
  @Event() indeterminateChange: EventEmitter<boolean>

  @Watch('indeterminate')
  updateIndeterminate(newValue: boolean) {
    const input = this.el.shadowRoot.querySelector('input')
    if (input !== null) {
      input.indeterminate = newValue
    }
  }

  componentWillLoad() {
    this.hasSlotContentDefault()
  }

  private hasSlotContentDefault(): void {
    this.slotDefault = this.el.innerHTML !== ''
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-checkbox': true,
      valid: this.state === 'valid',
      invalid: this.state === 'invalid',
      disabled: this.disabled,
      'hidden-label': this.hasHiddenLabel,
    }
  }

  private getAriaCheckedValue(): string {
    if (this.indeterminate) {
      return 'mixed'
    }
    return this.checked ? 'true' : 'false'
  }

  private readonly handleCheckedChange = () => {
    if (this.indeterminate) {
      // Sai do estado indeterminado ao clicar e define como marcado
      this.indeterminate = false
      this.checked = true
      this.indeterminateChange.emit(this.indeterminate)
    } else {
      // Alterna entre marcado e desmarcado
      this.checked = !this.checked
      this.indeterminate = false
    }
    this.checkedChange.emit(this.checked)
  }

  /**
   * Define o estado indeterminado do checkbox.
   * @param value Novo valor para o estado indeterminado.
   */
  @Method()
  async setIndeterminate(value: boolean) {
    this.indeterminate = value
    this.updateIndeterminate(value)
    this.indeterminateChange.emit(this.indeterminate)
  }

  /**
   * Inverte o valor da prop `checked`
   */
  @Method()
  async toggleChecked() {
    this.checked = !this.checked
    this.checkedChange.emit(this.checked)
  }

  /**
   * Define o número de checkboxes filhos em um grupo de checkboxes.
   * @param value Número de checkboxes filhos.
   */
  @Method()
  async setNumberOfChildren(value: number) {
    this.numberOfChildren = value
  }

  render() {
    return (
      <Host>
        <div class={this.getCssClassMap()}>
          <input
            type="checkbox"
            id={this.customId}
            name={this.name}
            checked={this.checked}
            disabled={this.disabled}
            value={this.value}
            onChange={this.handleCheckedChange}
            ref={(el) => el && (el.indeterminate = this.indeterminate)}
            aria-checked={this.getAriaCheckedValue()}
            aria-describedby={this.numberOfChildren > 0 ? 'children-number' : null}
          />
          <label htmlFor={this.customId} class={this.hasHiddenLabel ? 'visually-hidden' : undefined}>
            {this.slotDefault ? <slot></slot> : this.label}
          </label>
        </div>
        {this.isFather ? (
          <span id="children-number" class="sr-only">
            Número de opções: {this.numberOfChildren}
          </span>
        ) : null}
      </Host>
    )
  }
}

let checkboxId = 0
